import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import * as FileSaver from "file-saver";
import { of } from "rxjs";
import { map, tap } from "rxjs/operators";
import { Spinner } from "src/app/core/services/spinner.service";
import { RequestHelper, UtilitiesService } from "src/app/core/services/utilities.service";
import { GetAllResponse, SingleResponse } from "src/app/shared/model/restful-api.model";
import { ExcelService } from "src/app/shared/service/excel.service";
import { DateUtil } from "src/app/shared/utility/date.util";
import { environment } from "src/environments/environment";
import { Contract } from "../models/domain/contract.model";
import { ContractFormValue } from "../models/form/contract-form-value.model";
import { JPAPage } from "../models/pay-load/jpa.model";
import { AddCommentReq } from "../models/pay-load/request/comment.request";
import { FilterContractsReq } from "../models/pay-load/request/filter-contract-req.model";
import { Profile } from "src/app/shared/model/profile.model";
import { ProfileService } from "src/app/shared/service/profile.service";
import {
  ContractTypeSummaryRes,
  DashBoardRes,
  OverallSummaryRes
} from "../models/pay-load/response/chart.response";
import {
  AllMasterDataResponse,
  CommodityTier3Res,
  ContractItemResponse,
  OwnerProfile,
  UploadingAttachmentRes
} from "../models/pay-load/response/response.model";

@Injectable({
  providedIn: "root"
})
export class ContractService {
  public static readonly CONTRACT_MODULE_BASE_URL = environment.contractURL;
  private requestHelper: RequestHelper;
  constructor(
    private utilService: UtilitiesService,
    private excelService: ExcelService,
    private profileService: ProfileService,
    private httpClient: HttpClient,
    private spinner: Spinner
  ) {
    this.requestHelper = utilService.req;
  }

  fetchContractList(contractListRequest: FilterContractsReq) {
    const httpParams = this.createQueryParamsFromContrastListRequest(contractListRequest);
    return this.requestHelper
      .httpGet<SingleResponse<JPAPage<ContractItemResponse>>>(
        ContractService.CONTRACT_MODULE_BASE_URL + `/contract/search/${this.getUserProfile().userName}?` + httpParams.toString()
      )
      .pipe(map(response => response.data));
  }

  downloadContracts(contractListRequest: FilterContractsReq) {
    this.spinner.show();
    const httpParams = this.createQueryParamsFromContrastListRequest(contractListRequest);
    const url =
      ContractService.CONTRACT_MODULE_BASE_URL + "/contract/export?" + httpParams.toString();
    this.httpClient.get(url, { responseType: "arraybuffer" }).subscribe(fileData => {
      const fileName = "ContractRepository.xlsx";
      this.excelService.saveAsExcelFile(fileData, fileName);
      this.spinner.hide();
    });
  }

  downloadTemplate() {
    this.spinner.show();
    const url = ContractService.CONTRACT_MODULE_BASE_URL + "/contract/downloadTemplate";
    this.httpClient.get(url, { responseType: "arraybuffer" }).subscribe(fileData => {
      const fileName = "ContractRepository.xlsm";
      this.excelService.saveAsExcelFile(fileData, fileName);
      this.spinner.hide();
    });
  }

  getDashBoardData(request: FilterContractsReq) {
    const httpParams = this.createQueryParamsFromContrastListRequest(request);
    return this.requestHelper
      .httpGet<SingleResponse<DashBoardRes>>(
        ContractService.CONTRACT_MODULE_BASE_URL +
        `/contract/dashboard/get/${this.getUserProfile().userName}?` +
        httpParams.toString()
      )
      .pipe(map(response => response.data));
  }

  private createQueryParamsFromContrastListRequest(contractListRequest: FilterContractsReq) {
    let httpParams = new HttpParams();
    Object.keys(contractListRequest).forEach(function (key) {
      const isNullKey = contractListRequest[key] == null;
      if (isNullKey) {
        return;
      }

      const isEmptyStringKey =
        typeof contractListRequest[key] == "string" && contractListRequest[key].trim().length === 0;
      if (isEmptyStringKey) {
        return;
      }

      const isEmptyArrayKey =
        Array.isArray(contractListRequest[key]) && contractListRequest[key].length === 0;
      if (isEmptyArrayKey) {
        return;
      }

      const isKeyDateType = contractListRequest[key] instanceof Date;
      if (isKeyDateType) {
        httpParams = httpParams.append(
          key,
          "" + DateUtil.parseDateToTimestamp(contractListRequest[key])
        );
        return;
      }

      const isStringKey = typeof contractListRequest[key] === "string";
      if (isStringKey) {
        httpParams = httpParams.append(key, contractListRequest[key].trim());
        return;
      }

      httpParams = httpParams.append(key, contractListRequest[key]);
    });
    return httpParams;
  }

  fetchContract(contractID: string) {
    return this.requestHelper
      .httpGet<SingleResponse<Contract>>(
        ContractService.CONTRACT_MODULE_BASE_URL + "/contract/getById/" + contractID
      )
      .pipe(map(response => response.data));
  }

  fetchAllMasterData() {
    return this.requestHelper
      .httpGet<SingleResponse<AllMasterDataResponse>>(
        ContractService.CONTRACT_MODULE_BASE_URL + `/contract/master-data/getAll/${this.getUserProfile().userName}`
      )
      .pipe(map(response => response.data));
  }

  fetchSupplierNamesByKeyword(keyword: string) {
    const httpParams: HttpParams = new HttpParams().append("keyword", keyword);
    return this.requestHelper
      .httpGet<SingleResponse<string[]>>(
        ContractService.CONTRACT_MODULE_BASE_URL +
        "/contract/master-data/getSupplierName?" +
        httpParams.toString()
      )
      .pipe(map(response => response.data));
  }

  fetchGSCByKeyword(keyword: string) {
    const httpParams: HttpParams = new HttpParams().append("keyword", keyword);
    return this.requestHelper
      .httpGet<SingleResponse<string[]>>(
        ContractService.CONTRACT_MODULE_BASE_URL +
        "/contract/master-data/getSupplierGSC?" +
        httpParams.toString()
      )
      .pipe(map(response => response.data));
  }

  fetchSCXByKeyword(keyword: string) {
    const httpParams: HttpParams = new HttpParams().append("keyword", keyword);
    return this.requestHelper
      .httpGet<SingleResponse<string[]>>(
        ContractService.CONTRACT_MODULE_BASE_URL +
        "/contract/master-data/getSupplierSCX?" +
        httpParams.toString()
      )
      .pipe(map(response => response.data));
  }

  fetchCommodityTier3ByKeyword(keyword: string) {
    const httpParams: HttpParams = new HttpParams().append("keyword", keyword);
    return this.requestHelper
      .httpGet<SingleResponse<CommodityTier3Res[]>>(
        ContractService.CONTRACT_MODULE_BASE_URL +
        "/contract/master-data/getCommodityTier3?" +
        httpParams.toString()
      )
      .pipe(map(response => response.data));
  }

  fetchContractOwnersByKeyword(keyword: string) {
    const httpParams: HttpParams = new HttpParams().append("keyword", keyword);
    return this.requestHelper
      .httpGet<GetAllResponse<OwnerProfile>>(
        ContractService.CONTRACT_MODULE_BASE_URL +
        "/contract/user/getContractOwner?" +
        httpParams.toString()
      )
      .pipe(map(response => response.data));
  }

  getContractOwnersByKeywordAsValueAndText(keyword: string) {
    return this.fetchContractOwnersByKeyword(keyword).pipe(
      map(ownerProfiles => {
        return ownerProfiles.map(ownerProfile => ({
          value: ownerProfile.sso,
          text: ownerProfile.userName
        }));
      })
    );
  }

  fetchAllCommodityTier3s() {
    const httpParams: HttpParams = new HttpParams().append("keyword", "");
    return this.requestHelper
      .httpGet<SingleResponse<CommodityTier3Res[]>>(
        ContractService.CONTRACT_MODULE_BASE_URL +
        "/contract/master-data/getCommodityTier3?" +
        httpParams.toString()
      )
      .pipe(map(response => response.data));
  }

  saveContract(contractFormValue: ContractFormValue) {
    const requestBody = contractFormValue.toContractDTO();
    return this.requestHelper
      .httpPut<SingleResponse<number>>(
        ContractService.CONTRACT_MODULE_BASE_URL + "/contract/save",
        requestBody
      )
      .pipe(map(response => response.data));
  }

  saveContractAsDraft(contractFormValue: ContractFormValue) {
    const requestBody = contractFormValue.toContractDTO();
    return this.requestHelper
      .httpPut<SingleResponse<number>>(
        ContractService.CONTRACT_MODULE_BASE_URL + "/contract/saveDraft",
        requestBody
      )
      .pipe(map(response => response.data));
  }

  saveMass(smartSheetFile: File) {
    const formData = new FormData();
    formData.append("fileSmartSheet", smartSheetFile, smartSheetFile.name);

    const fileData$ = this.httpClient.post(
      ContractService.CONTRACT_MODULE_BASE_URL + "/contract/saveMass",
      formData,
      { observe: "response", responseType: "arraybuffer" }
    ).pipe(map(httpResponse => httpResponse.body));

    return fileData$.pipe(
      tap(fileData => {
        if (fileData.byteLength > 0) {
          const fileName = "ContractRepository-Error.xlsm";
          this.excelService.saveAsExcelFile(fileData, fileName);
        }
      }),
      map(res => {
        return res;
      })
    );
  }

  addComment(requestBody: AddCommentReq) {
    return this.requestHelper
      .httpPost<SingleResponse<void>>(
        ContractService.CONTRACT_MODULE_BASE_URL + "/contract/comment/add",
        requestBody
      )
      .pipe(map(response => response.data));
  }

  uploadContractAttachment(file: File) {
    const formData = new FormData();
    formData.append("file", file, file.name);
    return this.requestHelper.httpPost<UploadingAttachmentRes>(
      ContractService.CONTRACT_MODULE_BASE_URL + "/contract/document/upload",
      formData
    );
  }

  downloadAttachmentByS3FileIdAndFileName(s3FileId: string, fileName: string) {
    this.spinner.show();
    let httpParams = new HttpParams().append("fileId", s3FileId).append("fileName", fileName);
    const url =
      ContractService.CONTRACT_MODULE_BASE_URL +
      "/contract/document/download?" +
      httpParams.toString();
    const fileData$ = this.httpClient
      .post(url, null, { observe: "response", responseType: "arraybuffer" })
      .pipe(map(httpResponse => httpResponse.body));
    return fileData$.pipe(
      tap(fileData => {
        const file = new Blob([fileData], {
          type: "application/octet-stream"
        });
        FileSaver.saveAs(file, fileName);
        this.spinner.hide();
      }),
      map(() => {
        return;
      })
    );
  }

  downloadAttachmentsByContractId(contractId: string, requestNumber: string): void {
    this.spinner.show();
    this.httpClient
      .post(
        ContractService.CONTRACT_MODULE_BASE_URL + "/contract/document/downloadAll/" + contractId,
        null,
        { observe: "response", responseType: "arraybuffer" }
      )
      .pipe(map(httpResponse => httpResponse.body))
      .subscribe(fileData => {
        const file = new Blob([fileData], {
          type: "application/zip"
        });
        FileSaver.saveAs(file, requestNumber + "-" + "Attachments");
        this.spinner.hide();
      });
  }

  terminateContract(contractId: string) {
    return this.requestHelper.httpDelete<void>(
      ContractService.CONTRACT_MODULE_BASE_URL + "/contract/terminate/" + contractId
    );
  }

  reOpenContract(contractId: string) {
    return this.requestHelper.httpPut<void>(
      ContractService.CONTRACT_MODULE_BASE_URL + "/contract/reopen/" + contractId
    );
  }

  delegate(ownerSSO: string, newOwnerSSO: string) {
    const url = ContractService.CONTRACT_MODULE_BASE_URL + "/contract/delegate";
    const payload = {
      ownerFrom: ownerSSO,
      ownerTo: newOwnerSSO
    };
    return this.httpClient.put<void>(url, null, { params: payload });
  }

  private getUserProfile(): Profile {
    const profile = this.profileService.profileInfo.value.data;
    return new Profile(profile);
  }
}
